import { useState, useEffect, useCallback } from 'react';
import { SocialNetwork } from '../../domain/entities/social-network.entity';
import { CreateSocialNetworkDTO } from '../../application/dtos/social-networks/create-social-network.dto';
import { UpdateSocialNetworkDTO } from '../../application/dtos/social-networks/update-social-network.dto';
import { makeSocialNetworkUseCases } from '../../main/factories/social-network-dependencies.factory';
import { notify } from '../services/notification.service';

const useCases = makeSocialNetworkUseCases();

export const useSocialNetworks = () => {
  const [networks, setNetworks] = useState<SocialNetwork[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isReordering, setIsReordering] = useState<boolean>(false);

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedNetwork, setSelectedNetwork] = useState<SocialNetwork | null>(null);

  const fetchNetworks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await useCases.getSocialNetworksUseCase.execute();
      setNetworks(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar las redes sociales.';
      setError(message);
      notify.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNetworks();
  }, [fetchNetworks]);

  const q = searchQuery.toLowerCase().trim();
  const filteredNetworks = (
    q
      ? networks.filter(
          (sn) =>
            sn.name.toLowerCase().includes(q) ||
            sn.type.toLowerCase().includes(q) ||
            sn.url.toLowerCase().includes(q)
        )
      : [...networks]
  ).sort((a, b) => a.displayOrder - b.displayOrder);

  const total = networks.length;
  const active = networks.filter((sn) => sn.isActive).length;
  const inactive = total - active;
  const stats = { total, active, inactive };

  const handleCreate = async (data: CreateSocialNetworkDTO) => {
    try {
      await useCases.createSocialNetworkUseCase.execute(data);
      notify.success(`Red social "${data.name}" creada exitosamente.`);
      await fetchNetworks();
      setIsFormModalOpen(false);
      setSelectedNetwork(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al crear la red social.';
      notify.error(msg);
      throw err;
    }
  };

  const handleUpdate = async (id: string, data: UpdateSocialNetworkDTO) => {
    try {
      await useCases.updateSocialNetworkUseCase.execute(id, data);
      notify.success('Red social actualizada exitosamente.');
      await fetchNetworks();
      setIsFormModalOpen(false);
      setSelectedNetwork(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al actualizar la red social.';
      notify.error(msg);
      throw err;
    }
  };

  const handleToggleStatus = async (network: SocialNetwork) => {
    const newStatus = !network.isActive;
    try {
      await useCases.changeSocialNetworkStatusUseCase.execute(network.id, newStatus);
      notify.success(
        newStatus
          ? `Red social "${network.name}" activada.`
          : `Red social "${network.name}" desactivada.`
      );
      await fetchNetworks();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cambiar el estado.';
      notify.error(msg);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await useCases.deleteSocialNetworkUseCase.execute(id);
      notify.success('Red social eliminada exitosamente.');
      await fetchNetworks();
      setIsDeleteModalOpen(false);
      setSelectedNetwork(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar la red social.';
      notify.error(msg);
      throw err;
    }
  };

  const handleMoveUp = async (network: SocialNetwork) => {
    const sorted = [...networks].sort((a, b) => a.displayOrder - b.displayOrder);
    const index = sorted.findIndex((sn) => sn.id === network.id);
    if (index <= 0) return; // Ya está arriba

    const prev = sorted[index - 1];
    const updatedItems = [
      { id: network.id, displayOrder: prev.displayOrder },
      { id: prev.id, displayOrder: network.displayOrder },
    ];

    setIsReordering(true);
    try {
      await useCases.reorderSocialNetworksUseCase.execute(updatedItems);
      notify.success(`Orden de "${network.name}" actualizado.`);
      await fetchNetworks();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al reordenar.';
      notify.error(msg);
    } finally {
      setIsReordering(false);
    }
  };

  const handleMoveDown = async (network: SocialNetwork) => {
    const sorted = [...networks].sort((a, b) => a.displayOrder - b.displayOrder);
    const index = sorted.findIndex((sn) => sn.id === network.id);
    if (index === -1 || index >= sorted.length - 1) return; // Ya está abajo

    const next = sorted[index + 1];
    const updatedItems = [
      { id: network.id, displayOrder: next.displayOrder },
      { id: next.id, displayOrder: network.displayOrder },
    ];

    setIsReordering(true);
    try {
      await useCases.reorderSocialNetworksUseCase.execute(updatedItems);
      notify.success(`Orden de "${network.name}" actualizado.`);
      await fetchNetworks();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al reordenar.';
      notify.error(msg);
    } finally {
      setIsReordering(false);
    }
  };

  const openCreateModal = () => {
    setSelectedNetwork(null);
    setIsFormModalOpen(true);
  };

  const openEditModal = (network: SocialNetwork) => {
    setSelectedNetwork(network);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (network: SocialNetwork) => {
    setSelectedNetwork(network);
    setIsDeleteModalOpen(true);
  };

  const closeModals = () => {
    setIsFormModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedNetwork(null);
  };

  return {
    networks: filteredNetworks,
    totalCount: networks.length,
    stats,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    isReordering,
    isFormModalOpen,
    isDeleteModalOpen,
    selectedNetwork,
    fetchNetworks,
    handleCreate,
    handleUpdate,
    handleToggleStatus,
    handleDelete,
    handleMoveUp,
    handleMoveDown,
    openCreateModal,
    openEditModal,
    openDeleteModal,
    closeModals,
  };
};
